import type { CreditConsultation, ScoreBand } from '@/shared/api/resources';
import { Badge } from '@/shared/ui/Badge';

import {
  caminhoDoArco,
  ESCALA_MAXIMA,
  faixaDoScore,
  faixasEmOrdem,
  naEscala,
  pontoDoArco,
  trechosDoMedidor,
  type TrechoDoMedidor,
} from './faixasDoMedidor';
import { Info } from './shared';
import { textoDoValorDaConsulta as texto } from './valorDaConsulta';

/** Os scores da Vigidata, na ordem da consulta. */
const scores = [
  { elemento: 'vgd_score_d00', rotulo: 'Score D00' },
  { elemento: 'vgd_score_d30', rotulo: 'Score D30' },
  { elemento: 'vgd_score_d60', rotulo: 'Score D60' },
];

/** As classes em nomes inteiros, para o Tailwind gerar cada cor. */
const coresDaClasse: Record<string, { traco: string; fundo: string }> = {
  D: { traco: 'stroke-score-d', fundo: 'bg-score-d' },
  DD: { traco: 'stroke-score-dd', fundo: 'bg-score-dd' },
  C: { traco: 'stroke-score-c', fundo: 'bg-score-c' },
  CC: { traco: 'stroke-score-cc', fundo: 'bg-score-cc' },
  B: { traco: 'stroke-score-b', fundo: 'bg-score-b' },
  BB: { traco: 'stroke-score-bb', fundo: 'bg-score-bb' },
  A: { traco: 'stroke-score-a', fundo: 'bg-score-a' },
  AA: { traco: 'stroke-score-aa', fundo: 'bg-score-aa' },
};

const SEM_COR = { traco: 'stroke-ink-200', fundo: 'bg-ink-200' };

const corDe = (classe: string | undefined) =>
  (classe ? coresDaClasse[classe] : undefined) ?? SEM_COR;

// Geometria do desenho, nas unidades do viewBox.
const LARGURA = 256;
const ALTURA = 138;
const CENTRO = { x: 128, y: 126 };
const RAIO = 92;
const TRACO = 12;
const TRACO_DA_FAIXA_DO_SCORE = 18;
const RAIO_DAS_LETRAS = RAIO + 22;
/** A folga entre trechos, em pontos da escala: cerca de 2 unidades de desenho. */
const FOLGA = 3;
/** Faixa mais estreita que isso nao tem letra no arco: ela fica so na legenda. */
const LARGURA_MINIMA_DA_LETRA = 30;

function Trecho({ trecho, ativo }: { trecho: TrechoDoMedidor; ativo: boolean }) {
  const folga = Math.min(FOLGA, (trecho.fim - trecho.inicio) / 4);
  return (
    <path
      d={caminhoDoArco(trecho.inicio + folga, trecho.fim - folga, CENTRO, RAIO)}
      fill="none"
      strokeWidth={ativo ? TRACO_DA_FAIXA_DO_SCORE : TRACO}
      className={corDe(trecho.faixa?.class).traco}
    />
  );
}

function LetraDaFaixa({ trecho, ativo }: { trecho: TrechoDoMedidor; ativo: boolean }) {
  if (!trecho.faixa || trecho.fim - trecho.inicio < LARGURA_MINIMA_DA_LETRA)
    return null;
  const ponto = pontoDoArco((trecho.inicio + trecho.fim) / 2, CENTRO, RAIO_DAS_LETRAS);
  return (
    <text
      x={ponto.x}
      y={ponto.y}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      className={ativo ? 'fill-ink-900 font-semibold' : 'fill-ink-500'}
    >
      {trecho.faixa.class}
    </text>
  );
}

/** O que escrever embaixo do medidor, com a falta de classe dita como falta. */
function leituraDoScore(score: number, faixa: ScoreBand | null, decide: boolean) {
  if (!naEscala(score)) {
    return {
      titulo: 'Fora da escala',
      detalhe: `A escala vai de 0 a ${ESCALA_MAXIMA}`,
    };
  }
  if (!faixa) {
    return decide
      ? { titulo: 'Sem classe', detalhe: 'Fora das faixas: analise manual' }
      : { titulo: 'Sem faixa', detalhe: 'Fora das faixas da regra' };
  }
  return {
    titulo: `${decide ? 'Classe' : 'Faixa'} ${faixa.class}`,
    detalhe: `De ${faixa.min} a ${faixa.max}`,
  };
}

/** Um score no meio circulo das faixas: a cor e a escala vem da regra, nao da tela. */
export function MedidorDeScore({
  rotulo,
  score,
  faixas,
  decide,
}: {
  rotulo: string;
  score: number;
  faixas: ScoreBand[];
  decide: boolean;
}) {
  const faixa = naEscala(score) ? faixaDoScore(score, faixas) : null;
  const leitura = leituraDoScore(score, faixa, decide);
  const marcador = naEscala(score) ? pontoDoArco(score, CENTRO, RAIO) : null;
  const trechos = trechosDoMedidor(faixas);
  const ativo = (t: TrechoDoMedidor) => t.faixa !== null && t.faixa === faixa;
  return (
    <div className="flex flex-col items-center gap-1 rounded-md border border-line p-3">
      <dt className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
        {rotulo}
      </dt>
      <dd className="flex w-full flex-col items-center gap-1">
        <svg
          viewBox={`0 0 ${LARGURA} ${ALTURA}`}
          role="img"
          aria-label={`${rotulo}: ${score} de ${ESCALA_MAXIMA}. ${leitura.titulo}. ${leitura.detalhe}.`}
          className="w-full max-w-64"
        >
          {trechos.map((t) => (
            <Trecho key={t.inicio} trecho={t} ativo={ativo(t)} />
          ))}
          {trechos.map((t) => (
            <LetraDaFaixa key={t.inicio} trecho={t} ativo={ativo(t)} />
          ))}
          {marcador ? (
            <circle
              cx={marcador.x}
              cy={marcador.y}
              r={7}
              strokeWidth={3}
              className="fill-surface stroke-ink-800"
            />
          ) : null}
          <text
            x={CENTRO.x}
            y={CENTRO.y - 16}
            textAnchor="middle"
            fontSize={44}
            className="fill-ink-900 font-bold"
          >
            {score}
          </text>
          <text
            x={CENTRO.x}
            y={CENTRO.y + 2}
            textAnchor="middle"
            fontSize={12}
            className="fill-ink-500"
          >
            de {ESCALA_MAXIMA}
          </text>
        </svg>
        <p className="flex items-center gap-1.5 text-sm font-semibold text-ink-900">
          <span
            aria-hidden="true"
            className={`size-2.5 rounded-xs ${corDe(faixa?.class).fundo}`}
          />
          {leitura.titulo}
        </p>
        <p className="text-2xs text-ink-500">{leitura.detalhe}</p>
        {decide ? <Badge tone="info">Decide a classe do motor</Badge> : null}
      </dd>
    </div>
  );
}

/**
 * Os tres scores da consulta nas faixas de Score e rating da versao ativa, que cada
 * empresa ajusta nas regras dela. So mostra: quem decide a classe e o motor (CRD-003).
 */
export function MedidoresDeScore({
  consulta,
  faixas,
  elementoDaClasse,
}: {
  consulta: CreditConsultation;
  faixas: ScoreBand[];
  elementoDaClasse: string;
}) {
  const decideAlgum = scores.some((s) => s.elemento === elementoDaClasse);
  return (
    <div className="flex flex-col gap-3">
      <dl className="grid gap-3 sm:grid-cols-3">
        {scores.map(({ elemento, rotulo }) => {
          const valor = consulta.values?.find((v) => v.element === elemento);
          const score = valor?.situation === 'encontrado' ? valor.value_integer : null;
          if (score === null || score === undefined) {
            return <Info key={elemento} label={rotulo} value={texto(valor)} />;
          }
          return (
            <MedidorDeScore
              key={elemento}
              rotulo={rotulo}
              score={score}
              faixas={faixas}
              decide={elemento === elementoDaClasse}
            />
          );
        })}
      </dl>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-2xs text-ink-700">
        {faixasEmOrdem(faixas).map((f) => (
          <li key={f.class} className="flex items-center gap-1">
            <span
              aria-hidden="true"
              className={`size-2.5 rounded-xs ${corDe(f.class).fundo}`}
            />
            <strong>{f.class}</strong> {f.min} a {f.max}
          </li>
        ))}
      </ul>
      <p className="text-2xs text-ink-500">
        Faixas de Score e rating da versao ativa da empresa.{' '}
        {decideAlgum
          ? 'So o score marcado decide a classe do motor; os outros mostram em que faixa caem.'
          : 'Nesta versao nenhum score da SeguraCoreCredit decide a classe: os medidores mostram em que faixa cada um cai.'}
      </p>
    </div>
  );
}
